import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";

// The Android back button closes a full-screen overlay that is not a route
// (the venue search, 1.5.3) instead of stepping the page behind it back.
//
// With no listener, Capacitor's App plugin steps the webview's history on a
// back press; while ANY `backButton` listener is registered it hands every
// press to the listeners instead. So the listener lives only while the
// overlay is open, and the app's usual back behaviour returns the moment it
// closes. iOS has no back button; on the web this does nothing (Escape and
// the on-screen back arrow cover it there).
//
// The call returns a real promise: chain on it, never await the plugin
// object itself (reference: a Capacitor plugin proxy fakes `then`).
export function useAndroidBack(onBack: () => void): void {
  const backRef = useRef(onBack);
  backRef.current = onBack;
  useEffect(() => {
    if (Capacitor.getPlatform() !== "android") return;
    let closed = false;
    let remove: (() => void) | null = null;
    App.addListener("backButton", () => backRef.current())
      .then((handle) => {
        // Closed before the listener landed: take it straight back off.
        if (closed) handle.remove().catch(() => {});
        else remove = () => handle.remove().catch(() => {});
      })
      .catch(() => {});
    return () => {
      closed = true;
      remove?.();
    };
  }, []);
}
